import { ContinuousSessionPicker } from 'components/molecules/ContinuousSessionPicker';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import NotFoundExperience from 'components/NotFoundExperience/NotFoundExperience';
import ResourceGroupList from 'components/Resource/ResourceGroupList';
import { PrimaryButton } from 'components/theme/Button';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { Flex, Sep } from 'components/common/styled/common-styled';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { format } from 'date-fns';
import useResourceGroupAttributeLabels from 'api/experience/useResourceGroupAttributeLabels';
import { useResourceMapContext } from 'features/Reserve/contexts/ResourceMapContext';
import useAvailableDates from 'features/Reserve/hooks/useAvailableDates';
import useGroupViewEvent from 'features/Reserve/hooks/useGroupViewEvent';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { isResourceAvailable } from 'features/Reserve/utils/groupAvailability';
import useContinuousOptions from 'hooks/useContinuousOptions';
import useStayQuote from 'hooks/useStayQuote';
import { buildCanonicalPath, useCanonicalLink } from 'hooks/useCanonicalLink';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DATE_FORMAT } from 'utils';
import { isDayBasedTimeFrame } from 'utils/continuousSelection';
import { formatEventDateLong } from 'utils/dateTime';
import { ResourceGroupPageSkeleton } from '../Skeleton';

/**
 * The group page of a continuously booked resource group on a map. The guest picks a date and a start
 * and length (or a stay), then one of the group's resources by its number or on the map, and goes on to
 * that resource's page, where the price tiers are.
 */
export const MappedContinuousGroupPage = () => {
  const {
    availabilityRule,
    collection,
    collectionId,
    continuousSelection,
    date,
    duration,
    groupId,
    isInitialLoading,
    isLoadingListing,
    latest,
    listing,
    resourceGroup,
    selectedSession,
    sessions,
    setResourceBookingSessionParams,
    time,
    timeFrame,
  } = useResourceBookingSession();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const { selectedResource, selectResource, unselectResource } =
    useResourceMapContext();
  const path = buildCanonicalPath({ collectionId, groupId });
  useCanonicalLink(path);

  const [calendarMonth, setCalendarMonth] = useState<moment.Moment>(() =>
    moment(date ?? undefined),
  );
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    if (date) {
      setCalendarMonth(moment(date));
    }
  }, [date]);

  // A day-based stay is picked on a two-month calendar, so its availability covers the next month too.
  const isDayBased = isDayBasedTimeFrame(timeFrame);
  const { availableDates, isLoadingAvailableDates } = useAvailableDates({
    collectionId: listing?.resourceGroupCollectionId,
    from: calendarMonth.clone().startOf('month').format(DATE_FORMAT),
    latest,
    resourceGroupId: groupId,
    to: calendarMonth
      .clone()
      .add(isDayBased ? 1 : 0, 'month')
      .endOf('month')
      .format(DATE_FORMAT),
  });

  const handleSessionChange = useCallback(
    (nextTime: string | undefined, nextDuration: number) => {
      setResourceBookingSessionParams({
        duration: nextDuration,
        time: nextTime,
      });
    },
    [setResourceBookingSessionParams],
  );

  const handleStayChange = useCallback(
    (nextDate: string, nextDuration: number) => {
      setResourceBookingSessionParams({
        date: nextDate,
        duration: nextDuration,
        time: undefined,
      });
    },
    [setResourceBookingSessionParams],
  );

  // The page opens on a start and length, or a span of days, so its product view reports what each tier
  // costs for it, priced by the server as the guest will see it; nothing is picked on this page but the
  // dates, so each tier is quoted for one.
  const quoteTiers = useMemo(
    () =>
      (selectedSession?.priceTiers ?? []).map(({ id, name }) => ({
        id,
        name,
        participants: 0,
      })),
    [selectedSession?.priceTiers],
  );
  const { quote } = useStayQuote({
    enabled: true,
    experienceId: continuousSelection?.spans[0]?.experienceId,
    selection: continuousSelection,
    tiers: quoteTiers,
  });
  useGroupViewEvent({
    listing,
    requiresQuotedPrice: true,
    resourceGroup,
    session: selectedSession,
    priceByTierName: quote?.stayPriceByTierName,
  });

  const { durationOptions, onDurationSelect, onStartSelect, startOptions } =
    useContinuousOptions({
      date,
      durations: resourceGroup?.durations ?? [],
      onChange: handleSessionChange,
      rule: availabilityRule,
      selection: continuousSelection,
      spans: sessions,
      timeFrame,
    });

  // The resources that can take the stay in force; the numbers on the map are the same ones.
  const resourceListItems = useMemo(
    () =>
      (resourceGroup?.experiences ?? [])
        .filter((experience) =>
          isResourceAvailable({
            continuousSelection,
            duration,
            isContinuous: true,
            resourceId: experience.id,
            sessions,
            time,
          }),
        )
        .map((experience) => ({
          id: experience.id,
          name: experience.title ?? '',
          number: experience.number ?? '',
        })),
    [continuousSelection, duration, resourceGroup?.experiences, sessions, time],
  );

  const attributeLabels = useResourceGroupAttributeLabels({
    experienceIds: useMemo(
      () =>
        (resourceGroup?.experiences ?? []).map((experience) => experience.id),
      [resourceGroup?.experiences],
    ),
    resourceGroupId: groupId,
  });
  const selectedResourceItem = resourceListItems.find(
    (item) => item.id === selectedResource,
  );
  const selectedResourceAttributes = selectedResource
    ? attributeLabels.get(selectedResource)
    : undefined;

  // A resource picked for one stay can be taken by the next one the guest tries.
  useEffect(() => {
    if (
      selectedResource &&
      !resourceListItems.some((item) => item.id === selectedResource)
    ) {
      unselectResource();
    }
  }, [resourceListItems, selectedResource, unselectResource]);

  const handleResourceClick = useCallback(
    (resourceId: string) => {
      if (selectedResource === resourceId) {
        unselectResource();
        return;
      }
      selectResource(resourceId);
    },
    [selectedResource, selectResource, unselectResource],
  );

  const handleReserveClick = useCallback(() => {
    if (!selectedResource) {
      return;
    }
    setResourceBookingSessionParams({ resourceId: selectedResource });
  }, [selectedResource, setResourceBookingSessionParams]);

  const handleSelectDate = useCallback(
    (nextDate: Date) => {
      if (isLoadingAvailableDates) return;
      setResourceBookingSessionParams({
        date: moment(nextDate).format(DATE_FORMAT),
      });
      setShowDatePicker(false);
    },
    [isLoadingAvailableDates, setResourceBookingSessionParams],
  );

  const handlePrevNextMonth = useCallback((month: Date) => {
    setCalendarMonth(moment(month));
  }, []);

  const isDateDisabled = useCallback(
    (day: Date) =>
      !availableDates.includes(format(day, 'yyyy-MM-dd')) ||
      isLoadingAvailableDates,
    [availableDates, isLoadingAvailableDates],
  );

  if (groupId && !isLoadingListing && collection && !resourceGroup) {
    return <NotFoundExperience />;
  }

  if (isLoadingListing || isInitialLoading || !resourceGroup || !listing) {
    return <ResourceGroupPageSkeleton />;
  }

  const hasStay = !!continuousSelection;

  return (
    <Content data-testid="mapped-continuous-group-page">
      <Flex alignItem="flex-start" direction="column" gap={12}>
        <ResourceHeading2 className="resource-title">
          {resourceGroup.title}
        </ResourceHeading2>
        {date && !isDayBased && (
          <DayPickerPopover
            align="start"
            asChild
            defaultMonth={moment(date).toDate()}
            footer={null}
            isLoading={isLoadingAvailableDates}
            modifiers={{ disabled: isDateDisabled }}
            mode="single"
            onNextClick={handlePrevNextMonth}
            onOpenChange={setShowDatePicker}
            onPrevClick={handlePrevNextMonth}
            onSelect={handleSelectDate}
            open={showDatePicker}
            required
            selected={moment(date).toDate()}
            side="bottom"
            sideOffset={10}
            trigger={
              <DateTrigger
                className="current-date"
                fontSize="16px"
                fontWeight="400"
                lineHeight="20px"
              >
                {formatEventDateLong(date, locale)}
              </DateTrigger>
            }
          />
        )}
      </Flex>

      {availabilityRule && timeFrame && date && (
        <ContinuousSessionPicker
          availableDates={availableDates}
          date={date}
          durationOptions={durationOptions}
          isLoadingAvailableDates={isLoadingAvailableDates}
          onDurationSelect={onDurationSelect}
          onMonthChange={handlePrevNextMonth}
          onRangeChange={handleStayChange}
          onStartSelect={onStartSelect}
          rule={availabilityRule}
          selection={continuousSelection}
          startOptions={startOptions}
          timeFrame={timeFrame}
        />
      )}

      <Sep />

      <Flex alignItem="stretch" direction="column" gap={12}>
        {hasStay && resourceListItems.length === 0 ? (
          <ResourceParagraph
            className="resource-list-empty"
            fontSize="14px"
            fontWeight="400"
            lineHeight="20px"
          >
            {translate('noGroupAvailableOnThisDateAndTime', {
              groupTitle: resourceGroup.title,
            })}
          </ResourceParagraph>
        ) : (
          <ResourceGroupList
            onResourceListItemClick={handleResourceClick}
            resourceList={resourceListItems}
            truncateLimit={7}
          />
        )}
      </Flex>

      {selectedResourceItem && selectedResourceAttributes && (
        <ResourceParagraph
          className="selected-resource-attributes"
          fontSize="14px"
          fontWeight="400"
          lineHeight="20px"
        >
          {`${
            selectedResourceItem.name || selectedResourceItem.number
          } · ${selectedResourceAttributes}`}
        </ResourceParagraph>
      )}

      <PrimaryButton
        aria-label={translate('reserveSelectedSlot')}
        big
        className="reserve-button"
        disabled={!hasStay || !selectedResource}
        onClick={handleReserveClick}
      >
        {listing.customCallToAction || translate('reserve')}
      </PrimaryButton>
    </Content>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const DateTrigger = styled(ResourceParagraph)`
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
`;
